import { Dropdown, Steps, Text } from '@components';
import { useUpdateUserInfoMutation } from '@features/api/userSlice';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, useTheme } from '@mui/material';
import { registerInput } from '@utils/reactHookForm';
import { getErrorProps } from '@utils/validation/validationFunctions';
import { contactDetailsFormSchema } from '@validationSchemas/investment';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import InvestmentFooter from '../InvestmentFooter/InvestmentFooter';
import {
  ContentContainer,
  InputContainer,
  StyledInput,
} from './ContactDetails.styles';
import { ContactDetailsFormData } from './ContactDetails.types';
import countriesJSON from '@assets/countries.json';
import { useMemo } from 'react';
import { getFlagEmoji } from '@utils/functions/getFlagFromCountryCode';
import WithUser, { WithUserProps } from '@HOCs/withUser';
import { useCountriesQuery } from '@features/api/appDataSlice';
import { getPersonalDetailLockNavigation } from '../SubscriptionIntro/SubscriptionIntro.utils';
import { InvestmentContainer } from '../Investment.styles';
import { TOTAL_STEPS } from '../Investment.consts';

const ContactDetails: React.FC<WithUserProps> = ({ user }) => {
  const theme = useTheme();
  const intl = useIntl();
  const navigate = useNavigate();
  const { productId } = useParams();

  const [updateUserInfoMutation] = useUpdateUserInfoMutation();
  const { data: countries } = useCountriesQuery();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isValid },
  } = useForm<ContactDetailsFormData>({
    resolver: yupResolver(contactDetailsFormSchema),
    mode: 'onChange',
    shouldFocusError: false,
    defaultValues: {
      streetAddress: user.contactDetails?.streetAddress,
      city: user.contactDetails?.city,
      state: user.contactDetails?.state,
      zipCode: user.contactDetails?.zipCode,
      country: user.contactDetails?.country,
      phoneCountryCode: user.contactDetails?.phoneCountryCode,
      phoneNumber: user.contactDetails?.phoneNumber,
    },
  });

  const [countryDialCodesWithFlag] = useMemo(() => {
    const countriesArray = countriesJSON as Array<any>;
    return [
      countriesArray.map((x) => ({
        name: `${x.name} (${x.dialling_code})`,
        value: x.dialling_code,
        icon: getFlagEmoji(x.code),
      })) ?? [],
    ];
  }, []);

  const countriesMenuItems = useMemo(() => {
    return (
      countries?.map((x) => ({
        name: x.name,
        value: x.name,
        icon: x.flag,
      })) ?? []
    );
  }, [countries]);

  const onSubmit = async (data: ContactDetailsFormData) => {
    try {
      await updateUserInfoMutation({
        ...user,
        contactDetails: {
          ...user.contactDetails,
          ...data,
        },
      }).unwrap();

      navigate(`/investment-subscription/${productId}/tax-details`, {
        state: {
          progress: 4,
        },
        replace: true,
      });
    } catch (e) {
      console.error(e);
    }
  };
  if (user.personalDetailsLocked) {
    getPersonalDetailLockNavigation(navigate, productId)();
  }
  return (
    <InvestmentContainer data-testid='contact-details-container'>
      <ContentContainer>
        <Steps currentStep={4} totalSteps={TOTAL_STEPS} />
        <Text
          variant='h3'
          fontWeight='fontWeightBold'
          sx={{ mt: { xs: 16, md: 5 } }}
          color={theme.palette.black.A100}
        >
          <FormattedMessage id='investment.contactDetails.contactDetails' />
        </Text>
        <StyledInput
          label={intl.formatMessage({
            id: 'investment.contactDetails.streetAddress',
          })}
          sx={{ marginTop: 10 }}
          inputProps={{ 'data-testid': 'street-address-input' }}
          {...registerInput(register, 'streetAddress')}
          {...getErrorProps(intl, errors.streetAddress)}
        />
        <InputContainer>
          <StyledInput
            label={intl.formatMessage({
              id: 'investment.contactDetails.city',
            })}
            fullWidth
            inputProps={{ 'data-testid': 'city-input' }}
            {...registerInput(register, 'city')}
            {...getErrorProps(intl, errors.city)}
          />
          <StyledInput
            label={intl.formatMessage({
              id: 'investment.contactDetails.state',
            })}
            fullWidth
            inputProps={{ 'data-testid': 'state-input' }}
            {...registerInput(register, 'state')}
            {...getErrorProps(intl, errors.state)}
          />
        </InputContainer>
        <InputContainer justifyContent='space-between'>
          <StyledInput
            label={intl.formatMessage({
              id: 'investment.contactDetails.zipCode',
            })}
            fullWidth
            inputProps={{ 'data-testid': 'zip-code-input' }}
            sx={{ maxWidth: { xs: '100%', md: 'calc(50% - 10px)' } }}
            {...registerInput(register, 'zipCode')}
            {...getErrorProps(intl, errors.zipCode)}
          />
          <Box sx={{ width: { xs: '100%', md: 'calc(50% - 10px)' } }}>
            <Controller
              control={control}
              name='country'
              render={({ field }) => (
                <Dropdown
                  label={intl.formatMessage({
                    id: 'investment.contactDetails.country',
                  })}
                  inputProps={{ 'data-testid': 'country-dropdown' }}
                  menuItemData={countriesMenuItems}
                  {...field}
                />
              )}
            />
          </Box>
        </InputContainer>
        <InputContainer>
          <Box sx={{ width: { xs: '100%', md: '35%' }, minWidth: 160 }}>
            <Controller
              control={control}
              name='phoneCountryCode'
              render={({ field }) => (
                <Dropdown
                  label={intl.formatMessage({
                    id: 'investment.contactDetails.phoneCountryCode',
                  })}
                  inputProps={{ 'data-testid': 'phone-country-code' }}
                  menuItemData={countryDialCodesWithFlag}
                  {...field}
                />
              )}
            />
          </Box>
          <StyledInput
            label={intl.formatMessage({
              id: 'investment.contactDetails.mobileNumber',
            })}
            sx={{ width: { xs: '100%', md: '65%' } }}
            inputProps={{ 'data-testid': 'phone-number-input' }}
            {...registerInput(register, 'phoneNumber')}
            {...getErrorProps(intl, errors.phoneNumber)}
          />
        </InputContainer>
      </ContentContainer>
      <InvestmentFooter
        secondaryButton={{
          title: intl.formatMessage({
            id: 'misc.previous',
          }),
          onClick: () =>
            navigate(`/investment-subscription/${productId}/personal-details`, {
              state: {
                progress: 2,
              },
              replace: true,
            }),
        }}
        primaryButton={{
          title: intl.formatMessage({
            id: 'misc.next',
          }),
          disabled: !isValid,
          onClick: handleSubmit(onSubmit),
        }}
      />
    </InvestmentContainer>
  );
};

export default WithUser(ContactDetails);
